import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Grid, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";

import FilterBtn from "../../../../Assets/Icons/btn-filters.png";
import FilterBtnUp from "../../../../Assets/Icons/btn-filters-up.png";
import Filter from "../../../../Components/filters/OrderFilters/OrderFilter";
import { useNotification } from "../../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../../Hooks/useApiErrorHandler";
import httpclient from "../../../../Utils";
import { createFilterHandler } from "../../../../Utils/FilterUtils";

import OrderDetailsModal from "./OrderDetailsModal";
import { getColumns } from "./columns";
import { rows } from "./data";

const dataGridCustomStyles = {
  border: "none",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f5f5f5",
    borderRadius: 1,
  },
  "& .MuiDataGrid-cell": {
    color: "#383E49",
    fontFamily: "Lexend",
    fontWeight: 500,
  },
  "& .onhold-row": {
    backgroundColor: "#F45B691A",
    color: "#F45B69",
    "& .MuiDataGrid-cell": {
      color: "#F45B69",
      fontFamily: "Lexend",
      fontWeight: 500,
    },
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontFamily: "lexend",
    fontSize: "14px",
    fontWeight: 500,
    color: "#667085",
  },
  "& .MuiDataGrid-row": {
    cursor: "pointer",
  },
};

const INITIAL_FILTER_STATE = {
  sssJobNo: "",
  custOrder: "",
  customer: null,
  picker: null,
  bay: null,
  orderDateStart: null,
  orderDateEnd: null,
  cutoffStart: null,
  cutoffEnd: null,
  status: "",
  orderLineMin: "",
  orderLineMax: "",
  pickUpQtyMin: "",
  pickUpQtyMax: "",
};

const fieldMappings = {
  sssJobNo: ["sssJobNo", "$eq"],
  custOrder: ["custOrder", "$contains"],
  customer: ["customer", "$eq"],
  picker: ["picker", "$eq"],
  bay: ["bay", "$eq"],
  status: ["status", "$eq"],
};

const rangeFields = {
  orderDate: {
    apiField: "orderDate",
    minField: "orderDateStart",
    maxField: "orderDateEnd",
  },
  cutoff: {
    apiField: "cutoff",
    minField: "cutoffStart",
    maxField: "cutoffEnd",
  },
  orderLine: {
    apiField: "orderLine",
    minField: "orderLineMin",
    maxField: "orderLineMax",
  },
  pickUpQty: {
    apiField: "pickUpQty",
    minField: "pickUpQtyMin",
    maxField: "pickUpQtyMax",
  },
};

const OrdersTable = ({ type, title }) => {
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState(INITIAL_FILTER_STATE);
  const [submittedData, setSubmittedData] = useState({});

  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { isFromDashboard } = location.state || "";

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const fetchOrders = async () => {
    const API_URL = "getPlugins.php";

    try {
      const res = await httpclient.get(API_URL);

      const { data } = res;

      if (data.status === 200) {
        setOrders(data.data);
      } else {
        showNotification("error", data.msg);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // fetchOrders();
  }, []);

  const handleRowClick = (params) => {
    setSelectedOrder(params.row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChangeFilter = (field) => (eventOrValue) => {
    // Handle date pickers (eventOrValue is the Day.js object)
    if (
      field.includes("Date") ||
      field.includes("Start") ||
      field.includes("End")
    ) {
      setFilterData((prev) => ({
        ...prev,
        [field]: eventOrValue || null, // Store Day.js object or null
      }));
      return;
    }

    // Handle Autocomplete (eventOrValue is the selected value)
    if (field === "customer" || field === "picker" || field === "bay") {
      setFilterData((prev) => ({
        ...prev,
        [field]: eventOrValue || "",
      }));
      return;
    }

    // Handle Select dropdown (eventOrValue is the event)
    if (field === "status") {
      setFilterData((prev) => ({
        ...prev,
        [field]: eventOrValue.target.value,
      }));
      return;
    }

    // Handle regular text/number inputs (eventOrValue is the event)
    setFilterData((prev) => ({
      ...prev,
      [field]: eventOrValue.target.value,
    }));
  };

  const handleFilter = createFilterHandler({
    setLoading,
    setSubmittedData,
    setResults: setOrders,
    showNotification,
    handleApiError,
    httpClient: httpclient,
    endpoint: "getPlugins.php",
    fieldMappings,
    rangeFields,
  });

  const onFilterClick = () => {
    handleFilter(filterData);
  };

  const handleRemove = (field) => {
    setFilterData((prev) => ({
      ...prev,
      [field]:
        field === "sssJobNo" ||
        field === "custOrder" ||
        field === "status" ||
        field.includes("Min") ||
        field.includes("Max")
          ? ""
          : null,
    }));
    setSubmittedData((prev) => {
      const newData = { ...prev };
      delete newData[field];
      return newData;
    });
  };

  const handleClearFilters = () => {
    setFilterData(INITIAL_FILTER_STATE);
    setSubmittedData({});
  };

  const filteredRows = useMemo(() => {
    return rows
      .filter((row) => {
        if (type !== "All" && row.status !== type) return false;

        if (
          submittedData.sssJobNo &&
          !row.sssJobNo
            .toLowerCase()
            .includes(submittedData.sssJobNo.toLowerCase())
        ) {
          return false;
        }

        if (
          submittedData.custOrder &&
          !row.custOrder
            .toLowerCase()
            .includes(submittedData.custOrder.toLowerCase())
        ) {
          return false;
        }

        if (submittedData.customer && row.customer !== submittedData.customer) {
          return false;
        }

        if (submittedData.picker && row.picker.name !== submittedData.picker) {
          return false;
        }

        if (submittedData.bay && row.bay !== submittedData.bay) {
          return false;
        }

        if (submittedData.orderDateStart || submittedData.orderDateEnd) {
          const rowDate = dayjs(row.orderDate, "MM/DD/YYYY HH:mm");
          if (
            (submittedData.orderDateStart &&
              !submittedData.orderDateEnd &&
              !rowDate.isSame(submittedData.orderDateStart, "day")) ||
            (submittedData.orderDateStart &&
              submittedData.orderDateEnd &&
              !rowDate.isBetween(
                submittedData.orderDateStart,
                submittedData.orderDateEnd,
                "day",
                "[]"
              ))
          ) {
            return false;
          }
        }

        if (submittedData.cutoffStart || submittedData.cutoffEnd) {
          const [cutoff1, cutoff2] = row.cutoff
            .split("/")
            .map((time) => dayjs(time.trim(), "hh:mm A"));
          const hasMatch = [cutoff1, cutoff2].some((time) => {
            if (!time.isValid()) return false;
            if (submittedData.cutoffStart && !submittedData.cutoffEnd) {
              return (
                time.hour() === submittedData.cutoffStart.hour() &&
                time.minute() === submittedData.cutoffStart.minute()
              );
            }
            if (submittedData.cutoffStart && submittedData.cutoffEnd) {
              const startMinutes =
                submittedData.cutoffStart.hour() * 60 +
                submittedData.cutoffStart.minute();
              const endMinutes =
                submittedData.cutoffEnd.hour() * 60 +
                submittedData.cutoffEnd.minute();
              const timeMinutes = time.hour() * 60 + time.minute();
              return timeMinutes >= startMinutes && timeMinutes <= endMinutes;
            }
            return true;
          });
          if (!hasMatch) return false;
        }

        if (submittedData.status && row.status !== submittedData.status) {
          return false;
        }

        if (
          submittedData.orderLineMin &&
          row.orderLine < Number(submittedData.orderLineMin)
        ) {
          return false;
        }
        if (
          submittedData.orderLineMax &&
          row.orderLine > Number(submittedData.orderLineMax)
        ) {
          return false;
        }

        if (
          submittedData.pickUpQtyMin &&
          row.pickUpQty < Number(submittedData.pickUpQtyMin)
        ) {
          return false;
        }
        if (
          submittedData.pickUpQtyMax &&
          row.pickUpQty > Number(submittedData.pickUpQtyMax)
        ) {
          return false;
        }

        return true;
      })
      .sort(
        (a, b) => (b.isPriorityOrder === true) - (a.isPriorityOrder === true)
      );
  }, [submittedData, type]);

  const hasStatusOnHold = useMemo(() => {
    return filteredRows.some((row) => row.status === "On Hold");
  }, [filteredRows]);

  const columns = useMemo(() => getColumns(hasStatusOnHold), [hasStatusOnHold]);

  return (
    <>
      <Box
        sx={{
          width: "100%",
          padding: "24px 16px",
          backgroundColor: "white",
          borderRadius: "8px",
        }}
      >
        {isFromDashboard && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "16px",
            }}
          >
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </Box>
        )}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography
              variant="h6"
              sx={{ fontSize: "20px", fontWeight: 500, color: "#383E49" }}
            >
              {title}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            <img
              src={filterOpen ? FilterBtnUp : FilterBtn}
              alt="Filter"
              style={{ cursor: "pointer" }}
              onClick={() => setFilterOpen(!filterOpen)}
            />
          </Grid>
        </Grid>

        <Filter
          filterOpen={filterOpen}
          filterData={filterData}
          handleChangeFilter={handleChangeFilter}
          handleFilter={onFilterClick}
          handleClearFilters={handleClearFilters}
          handleRemove={handleRemove}
          submittedData={submittedData}
        />

        <Box sx={{ width: "100%", mt: 2 }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            getRowClassName={(params) =>
              params.row.status === "On Hold" ? "onhold-row" : ""
            }
            onRowClick={handleRowClick}
            pageSizeOptions={[10]}
            sx={dataGridCustomStyles}
          />
        </Box>
      </Box>

      {open && (
        <OrderDetailsModal
          open={open}
          handleClose={handleClose}
          selectedOrder={selectedOrder}
        />
      )}
    </>
  );
};

export default OrdersTable;
